import React, { useState } from 'react';
import api from '../services/api';

const CreateAppointmentModal = ({ isOpen, onClose, onCreated }) => {
  const [description, setDescription] = useState('');
  const [startTime, setStartTime] = useState('');
  const [duration, setDuration] = useState(50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isDevelopment = process.env.NODE_ENV === 'development';

  const roundToNext15Minutes = (dateTimeString) => {
    if (!dateTimeString || isDevelopment) return dateTimeString;

    const date = new Date(dateTimeString);
    const minutes = date.getMinutes();

    // Already on a 15-minute boundary
    if (minutes % 15 === 0) {
      return dateTimeString;
    }

    // Round up to next 15-minute mark
    const nextInterval = Math.ceil(minutes / 15) * 15;
    date.setMinutes(nextInterval, 0, 0); // Set minutes and reset seconds/ms

    // Convert back to datetime-local format
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const mins = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${mins}`;
  };

  const handleTimeBlur = () => {
    // Round when user finishes selecting (skip in development)
    if (startTime && !isDevelopment) {
      const roundedTime = roundToNext15Minutes(startTime);
      setStartTime(roundedTime);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate
    if (!description.trim()) {
      setError('Description is required');
      return;
    }

    if (description.length > 1000) {
      setError('Description must be less than 1000 characters');
      return;
    }

    if (!startTime) {
      setError('Start time is required');
      return;
    }

    // Check if start time is in the future
    const selectedTime = new Date(startTime);
    if (selectedTime <= new Date()) {
      setError('Start time must be in the future');
      return;
    }

    setLoading(true);

    try {
      // Convert datetime-local to ISO string (preserving local timezone)
      const localDate = new Date(startTime);
      const isoString = localDate.toISOString();

      const response = await api.post('/appointments', {
        description: description.trim(),
        start_time: isoString,
        duration_minutes: duration,
      });

      console.log('Appointment created:', response.data);

      // Reset form
      setDescription('');
      setStartTime('');
      setDuration(50);
      setError('');

      // Notify parent
      if (onCreated) {
        onCreated(response.data);
      }

      // Close modal
      onClose();
    } catch (err) {
      console.error('Error creating appointment:', err);
      setError(err.response?.data?.error || 'Failed to create appointment');
    } finally {
      setLoading(false);
    }
  };

  const getMinDateTime = () => {
    const now = new Date();
    // Convert to local timezone for datetime-local input
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-900">Create Appointment</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              &times;
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Description */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                What will you be working on? *
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g., Writing my weekly blog post"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                rows="4"
                maxLength={1000}
                required
              />
              <div className="text-xs text-gray-500 mt-1">
                {description.length}/1000 characters
              </div>
            </div>

            {/* Start Time */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                When? * <span className="font-normal text-gray-500">({Intl.DateTimeFormat().resolvedOptions().timeZone})</span>
              </label>
              <input
                type="datetime-local"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                onBlur={handleTimeBlur}
                min={getMinDateTime()}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
              <div className="text-xs text-gray-500 mt-1">
                {isDevelopment
                  ? 'Development mode: Select any time'
                  : 'Time will automatically round to 15-minute intervals (e.g., 2:00, 2:15, 2:30, 2:45)'}
              </div>
            </div>

            {/* Duration */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Duration *
              </label>
              <div className="flex gap-3">
                {[25, 50, 75].map((mins) => (
                  <button
                    key={mins}
                    type="button"
                    onClick={() => setDuration(mins)}
                    className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors ${
                      duration === mins
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {mins} min
                  </button>
                ))}
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating...' : 'Create Appointment'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateAppointmentModal;
